
import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { UserFormData, UserFormSchema } from '../../types/form';
import { UsersDetail, CreateUserRequest } from '../../types/settings/UsersTypes';
import { toastMessage } from '@/utils/toast-message';
import { createUser, updateUser } from '@/actions/data-fetch-users';
import { getTeams } from '@/actions/data-fetch-teams';
import Cookies from 'js-cookie';
import {
  User,
  Mail,
  Phone,
  Bell,
  Settings,
  Save,
  Loader2,
  CheckCircle,
  AlertCircle,
  Users,
  Camera
} from 'lucide-react';
import RoleSelect from '../common/InputField/RoleSelect';

interface ModernUserFormProps {
  onClose?: () => void;
  uuid?: string;
  initialData?: UsersDetail | null;
  onSuccess: (user: UsersDetail) => void;
  disabledFields?: {
    phone?: boolean;
    role?: boolean;
  };
}

const ModernUserForm: React.FC<ModernUserFormProps> = ({
  onClose,
  uuid,
  initialData,
  onSuccess,
  disabledFields = {}
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableTeams, setAvailableTeams] = useState<any[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [profilePic, setProfilePic] = useState<string | null>(null);
  const [isUploadingPic, setIsUploadingPic] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    register
  } = useForm<UserFormData>({
    resolver: zodResolver(UserFormSchema),
    defaultValues: {
      userName: '',
      userEmail: '',
      userPhone: '',
      role_uuid: '',
      team_uuid: '',
      user_slug: '',
      designation: '',
      description: '',
      counselor_type: null,
      expertise: '',
      leadNotifications: [],
      notificationsPaused: 'no',
      dailyEmailReports: []
    },
  });

  const leadNotifications = watch('leadNotifications');
  const notificationsPaused = watch('notificationsPaused');


  // Fetch available teams
  const fetchTeams = async () => {
    try {
      setLoadingTeams(true);
      const response = await getTeams({ limit: 100 });

      if (response.success || response.status === 'success') {
        const teams = response.data?.teams || [];
        setAvailableTeams(teams);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    } finally {
      setLoadingTeams(false);
    }
  };

  useEffect(() => {
    fetchTeams();
  }, []);

  useEffect(() => {
    if (initialData) {
      console.log('Setting form values with initialData:', initialData);
      setValue('userName', initialData.userName || '');
      setValue('userEmail', initialData.userEmail || '');
      setValue('userPhone', String(initialData.phone || ''));

      // Try role.uuid first, then fall back to role_uuid field directly
      const roleUuid = initialData.role?.uuid || (initialData as any).role_uuid || '';
      console.log('Setting role_uuid to:', roleUuid);
      setValue('role_uuid', roleUuid);

      setValue('team_uuid', initialData.team?.uuid || initialData.team_uuid || '');

      // Set counselor-specific fields
      setValue('user_slug', initialData.user_slug || '');
      setValue('designation', initialData.designation || '');
      setValue('description', initialData.description || '');
      setValue('counselor_type', initialData.counselor_type || null);
      setValue('expertise', initialData.expertise || '');

      const notifications: string[] = [];
      if (initialData.email_notification) notifications.push('email');
      if (initialData.sms_notification) notifications.push('sms');
      if (initialData.whatsapp_notifications) notifications.push('whatsapp');
      setValue('leadNotifications', notifications);

      setValue('notificationsPaused', initialData.user_pause ? 'yes' : 'no');
      setValue('dailyEmailReports', initialData.dailyEmailReports || []);

      // Set profile picture
      if (initialData.profile_pic && initialData.profile_pic !== 'user.png') {
        setProfilePic(initialData.profile_pic);
      }
    }
  }, [initialData]);

  const handleProfilePicClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toastMessage('Please select an image file', 'error');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toastMessage('Image size should be less than 2MB', 'error');
      return;
    }

    // For new users, we can't upload yet - need to create user first
    if (!uuid) {
      toastMessage('Please save the user first before uploading a profile picture', 'info');
      return;
    }

    setIsUploadingPic(true);
    try {
      const formData = new FormData();
      formData.append('image', file);

      const token = Cookies.get('crm_auth_token');
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/users/${uuid}/profile-picture`,
        {
          method: 'POST',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include',
          body: formData,
        }
      );

      const result = await response.json();

      if (result.status === 'success' && result.data?.url) {
        setProfilePic(result.data.url);
        toastMessage('Profile picture updated successfully', 'success');
      } else {
        throw new Error(result.message || 'Failed to upload profile picture');
      }
    } catch (error: any) {
      console.error('Error uploading profile picture:', error);
      toastMessage(error.message || 'Failed to upload profile picture', 'error');
    } finally {
      setIsUploadingPic(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const transformFormToApiData = (formData: UserFormData): CreateUserRequest => {
    return {
      userName: formData.userName,
      userEmail: formData.userEmail,
      phone: formData.userPhone,
      role_uuid: formData.role_uuid,
      team_uuid: formData.team_uuid || undefined,
      user_slug: formData.user_slug || undefined,
      designation: formData.designation || undefined,
      description: formData.description || undefined,
      counselor_type: formData.counselor_type || undefined,
      expertise: formData.expertise || undefined,
      email_notification: formData.leadNotifications.includes('email'),
      sms_notification: formData.leadNotifications.includes('sms'),
      whatsapp_notifications: formData.leadNotifications.includes('whatsapp'),
      user_pause: formData.notificationsPaused === 'yes',
    };
  };

  const onSubmit = async (formData: UserFormData) => {
    setIsSubmitting(true);
    try {
      const apiData = transformFormToApiData(formData);

      let response;
      if (uuid) {
        response = await updateUser(uuid, apiData);
      } else {
        response = await createUser(apiData);
      }

      if (response.status === 'success') {
        const role = {
          uuid: apiData.role_uuid,
          role_name: initialData?.role?.role_name || 'Role'
        };

        const updatedUser: UsersDetail = {
          ...initialData as UsersDetail,
          userName: apiData.userName,
          userEmail: apiData.userEmail,
          phone: apiData.phone,
          role,
          email_notification: apiData.email_notification,
          sms_notification: apiData.sms_notification,
          whatsapp_notifications: apiData.whatsapp_notifications,
          user_pause: apiData.user_pause,
          dailyEmailReports: initialData?.dailyEmailReports || []
        };

        onSuccess(updatedUser);
      } else {
        throw new Error(response.message || 'Failed to save user');
      }
    } catch (error: any) {
      toastMessage(error.message || 'Failed to save user', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const InputField = ({
    label,
    icon: Icon,
    error,
    disabled = false,
    description,
    children
  }: {
    label: string;
    icon: any;
    error?: string;
    disabled?: boolean;
    description?: string;
    children: React.ReactNode;
  }) => (
    <div className="space-y-2">
      <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
        <Icon className="w-4 h-4 mr-2 text-gray-400" />
        {label}
        {disabled && <span className="ml-2 text-xs text-gray-500">(Read-only)</span>}
      </label>
      {children}
      {error && (
        <div className="flex items-center text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="w-4 h-4 mr-1" />
          {error}
        </div>
      )}
      {description && !error && (
        <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
      )}
    </div>
  );

  const NotificationToggle = ({
    type,
    label,
    icon: Icon,
    description
  }: {
    type: string;
    label: string;
    icon: any;
    description: string;
  }) => {
    const isChecked = leadNotifications?.includes(type) || false;

    return (
      <div className="flex items-start space-x-3 p-4 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
        <div className="flex-shrink-0">
          <Icon className={`w-5 h-5 ${isChecked ? 'text-brandColor dark:text-brand-hover' : 'text-gray-400'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <label className="flex items-center cursor-pointer">
            <div className="flex items-center justify-between w-full">
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{label}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
              </div>
              <input
                type="checkbox"
                checked={isChecked}
                onChange={(e) => {
                  const current = leadNotifications || [];
                  if (e.target.checked) {
                    setValue('leadNotifications', [...current, type]);
                  } else {
                    setValue('leadNotifications', current.filter(n => n !== type));
                  }
                }}
                className="h-4 w-4 text-brandColor focus:outline-none focus:ring-1 focus:ring-brandColor border-gray-300 rounded"
              />
            </div>
          </label>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col relative">
      {/* Loading Overlay */}
      {isSubmitting && (
        <div className="absolute inset-0 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 flex items-center space-x-3">
            <Loader2 className="w-6 h-6 animate-spin text-brandColor" />
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {uuid ? 'Updating user...' : 'Creating user...'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Please wait while we save your changes
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Form Content - Single Page (No Tabs) */}
      <form onSubmit={handleSubmit(onSubmit)} className="flex-1 flex flex-col min-h-0">
        <div className={`flex-1 overflow-y-auto p-6 transition-opacity ${isSubmitting ? 'pointer-events-none opacity-60' : ''}`}>
          {/* Profile Information Section */}
          <div className="space-y-6 max-w-2xl">
            <div className="flex items-center mb-4">
              <User className="w-5 h-5 mr-2 text-brandColor" />
              <h3 className="text-base font-medium text-gray-900 dark:text-white">
                Profile Information
              </h3>
            </div>

            {/* Profile Picture */}
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div
                  onClick={handleProfilePicClick}
                  className={`w-20 h-20 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center cursor-pointer overflow-hidden hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border-2 border-dashed border-gray-300 dark:border-gray-500 ${!uuid ? 'opacity-50' : ''}`}
                >
                  {isUploadingPic ? (
                    <Loader2 className="h-8 w-8 animate-spin text-brandColor" />
                  ) : profilePic ? (
                    <img
                      src={profilePic}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="h-10 w-10 text-gray-400" />
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleProfilePicClick}
                  disabled={isUploadingPic || !uuid}
                  className="absolute bottom-0 right-0 bg-brandColor text-white p-1.5 rounded-full shadow-lg hover:bg-brand-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Camera className="h-3 w-3" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Profile Picture</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {uuid ? 'Click to upload (max 2MB)' : 'Save user first to upload'}
                </p>
              </div>
            </div>

            <InputField
              label="Full Name"
              icon={User}
              error={errors.userName?.message}
              description="The user's display name"
            >
              <input
                {...register('userName')}
                key="userName"
                type="text"
                autoComplete="off"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-brandColor dark:bg-gray-700 dark:text-white transition-colors"
                placeholder="Enter full name"
              />
            </InputField>

            <InputField
              label="Email Address"
              icon={Mail}
              error={errors.userEmail?.message}
              description="Primary email for login and notifications"
            >
              <input
                {...register('userEmail')}
                key="userEmail"
                type="email"
                autoComplete="off"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-brandColor dark:bg-gray-700 dark:text-white transition-colors"
                placeholder="user@example.com"
              />
            </InputField>

            <InputField
              label="Phone Number"
              icon={Phone}
              error={errors.userPhone?.message}
              disabled={disabledFields.phone}
              description={disabledFields.phone ? "Phone number cannot be changed after creation" : "Contact phone number"}
            >
              <input
                {...register('userPhone')}
                key="userPhone"
                type="tel"
                autoComplete="off"
                disabled={disabledFields.phone}
                className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-brandColor dark:bg-gray-700 dark:text-white transition-colors ${
                  disabledFields.phone ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' : ''
                }`}
                placeholder="+1 (555) 123-4567"
              />
            </InputField>

            <div className="space-y-2">
              <RoleSelect
                name="role_uuid"
                control={control}
                error={errors.role_uuid?.message}
                required
                disabled={disabledFields.role}
                roleStyling="w-full"
              />
              {disabledFields.role && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Role assignment is managed by administrators
                </p>
              )}
              {!disabledFields.role && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  User's access level and permissions
                </p>
              )}
            </div>

            <InputField
              label="Team"
              icon={Users}
              error={errors.team_uuid?.message}
              description="Assign user to a team"
            >
              <select
                {...register('team_uuid')}
                key="team_uuid"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-brandColor dark:bg-gray-700 dark:text-white transition-colors"
                disabled={loadingTeams}
              >
                <option value="">No Team Assigned</option>
                {availableTeams.map((team) => (
                  <option key={team.uuid} value={team.uuid}>
                    {team.team_name}
                  </option>
                ))}
              </select>
              {loadingTeams && (
                <div className="flex items-center text-xs text-gray-500 mt-1">
                  <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  Loading teams...
                </div>
              )}
            </InputField>
          </div>

          {/* Divider */}
          <hr className="my-8 border-gray-200 dark:border-gray-700" />

          {/* Counselor Information Section */}
          <div className="space-y-6 max-w-2xl">
            <div className="flex items-center mb-4">
              <User className="w-5 h-5 mr-2 text-brandColor" />
              <h3 className="text-base font-medium text-gray-900 dark:text-white">
                Counselor Information
              </h3>
            </div>

            <InputField
              label="URL Slug"
              icon={User}
              error={errors.user_slug?.message}
              description="Used for URL generation (/counsellors/[slug]). Leave empty to auto-generate."
            >
              <input
                {...register('user_slug')}
                key="user_slug"
                type="text"
                autoComplete="off"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-brandColor dark:bg-gray-700 dark:text-white transition-colors"
                placeholder="e.g., john-doe"
              />
            </InputField>

            <InputField
              label="Designation"
              icon={User}
              error={errors.designation?.message}
              description="Job title displayed on the counselor card"
            >
              <input
                {...register('designation')}
                key="designation"
                type="text"
                autoComplete="off"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-brandColor dark:bg-gray-700 dark:text-white transition-colors"
                placeholder="e.g., Senior Admission Counsellor"
              />
            </InputField>

            <InputField
              label="Description / Bio"
              icon={User}
              error={errors.description?.message}
              description="Brief description or bio displayed on the counselor profile"
            >
              <textarea
                {...register('description')}
                key="description"
                autoComplete="off"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-brandColor dark:bg-gray-700 dark:text-white transition-colors resize-none"
                placeholder="Brief description about this counselor..."
              />
            </InputField>

            <InputField
              label="Counselor Type"
              icon={Settings}
              error={errors.counselor_type?.message}
              description="Determines URL format: admission counsellors use /counsellors/[slug]"
            >
              <select
                {...register('counselor_type')}
                key="counselor_type"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-brandColor dark:bg-gray-700 dark:text-white transition-colors"
              >
                <option value="">Select Type</option>
                <option value="admission">Admission Counsellor</option>
                <option value="generic">Generic Counsellor</option>
              </select>
            </InputField>

            <InputField
              label="Expertise"
              icon={User}
              error={errors.expertise?.message}
              description="Areas of expertise shown on the counselor profile page"
            >
              <textarea
                {...register('expertise')}
                key="expertise"
                autoComplete="off"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-brandColor dark:bg-gray-700 dark:text-white transition-colors resize-none"
                placeholder="e.g., CBSE Admissions, IB Curriculum, Career Guidance..."
              />
            </InputField>
          </div>

          {/* Divider */}
          <hr className="my-8 border-gray-200 dark:border-gray-700" />

          {/* Notification Settings Section */}
          <div className="space-y-6 max-w-2xl">
            <div className="flex items-center mb-4">
              <Bell className="w-5 h-5 mr-2 text-brandColor" />
              <h3 className="text-base font-medium text-gray-900 dark:text-white">
                Notification Settings
              </h3>
            </div>

            {/* Notification Types */}
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Choose how you want to be notified about new leads and updates.
              </p>
              <div className="space-y-3">
                <NotificationToggle
                  type="email"
                  label="Email Notifications"
                  icon={Mail}
                  description="Receive notifications via email"
                />
                <NotificationToggle
                  type="sms"
                  label="SMS Notifications"
                  icon={Phone}
                  description="Receive notifications via text message"
                />
                <NotificationToggle
                  type="whatsapp"
                  label="WhatsApp Notifications"
                  icon={Bell}
                  description="Receive notifications via WhatsApp"
                />
              </div>
            </div>

            {/* Pause Account */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                Account Status
              </h4>
              <div className="flex items-start space-x-3 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                    Pause user account
                  </p>
                  <div className="space-y-2">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        value="no"
                        checked={notificationsPaused === 'no'}
                        onChange={() => setValue('notificationsPaused', 'no')}
                        className="h-4 w-4 text-brandColor focus:outline-none focus:ring-1 focus:ring-brandColor border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 flex items-center">
                        <CheckCircle className="w-4 h-4 mr-1 text-green-500" />
                        Keep account
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        value="yes"
                        checked={notificationsPaused === 'yes'}
                        onChange={() => setValue('notificationsPaused', 'yes')}
                        className="h-4 w-4 text-brandColor focus:outline-none focus:ring-1 focus:ring-brandColor border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 flex items-center">
                        <Settings className="w-4 h-4 mr-1 text-yellow-500" />
                        Pause account
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Summary */}
            <div className="bg-brandColor/10 dark:bg-brandColor/20 rounded-lg p-4">
              <h4 className="text-sm font-medium text-brandColor dark:text-brand-hover mb-2">
                Current Status
              </h4>
              <div className="text-sm text-brandColor dark:text-brand-hover">
                {notificationsPaused === 'yes' ? (
                  <span className="flex items-center">
                    <Settings className="w-4 h-4 mr-1" />
                    Account and all notifications are paused
                  </span>
                ) : (
                  <span className="flex items-center">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Account active - Notifications enabled for: {leadNotifications?.length > 0 ? leadNotifications.join(', ') : 'none selected'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions - Fixed at bottom */}
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800">
          <div className="flex items-center justify-end space-x-3">
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Cancel
              </button>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-brandColor border border-transparent rounded-lg hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {isSubmitting
                ? (uuid ? 'Updating...' : 'Creating...')
                : (uuid ? 'Update User' : 'Create User')
              }
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ModernUserForm;
